/**
 * Time spent in frequency bands from suspension velocity (windowed magnitude spectrum).
 */

const MIN_WINDOW = 128;
const MAX_WINDOW = 1024;
const DEFAULT_WINDOW = 512;
const HOP_RATIO = 0.5;

function medianDt(times) {
  const dts = [];
  for (let i = 1; i < times.length; i += 1) {
    const dt = times[i] - times[i - 1];
    if (dt > 0 && dt < 1) dts.push(dt);
  }
  if (!dts.length) return null;
  dts.sort((a, b) => a - b);
  return dts[Math.floor(dts.length / 2)];
}

/** Resample irregular series to uniform spacing at target Hz. */
function resampleUniform(values, times, sampleRateHz) {
  const n = Math.max(2, Math.floor((times[times.length - 1] - times[0]) * sampleRateHz) + 1);
  const t0 = times[0];
  const out = new Array(n).fill(0);
  let j = 0;
  for (let i = 0; i < n; i += 1) {
    const t = t0 + i / sampleRateHz;
    while (j < times.length - 1 && times[j + 1] < t) j += 1;
    const tA = times[j];
    const tB = times[Math.min(j + 1, times.length - 1)];
    const vA = values[j];
    const vB = values[Math.min(j + 1, values.length - 1)];
    if (tB <= tA || vA == null || vB == null) {
      out[i] = vA != null ? vA : vB != null ? vB : 0;
    } else {
      const w = (t - tA) / (tB - tA);
      out[i] = vA * (1 - w) + vB * w;
    }
  }
  return out;
}

function nextPow2(n) {
  let p = MIN_WINDOW;
  while (p < n) p *= 2;
  return Math.min(p, MAX_WINDOW);
}

function hann(i, n) {
  return 0.5 * (1 - Math.cos((2 * Math.PI * i) / (n - 1)));
}

/** In-place Cooley–Tukey radix-2 FFT (complex interleaved re, im). */
function fft(re, im) {
  const n = re.length;
  let j = 0;
  for (let i = 0; i < n; i += 1) {
    if (i < j) {
      const tr = re[i];
      re[i] = re[j];
      re[j] = tr;
      const ti = im[i];
      im[i] = im[j];
      im[j] = ti;
    }
    let m = n >> 1;
    while (m >= 1 && j >= m) {
      j -= m;
      m >>= 1;
    }
    j += m;
  }
  for (let size = 2; size <= n; size <<= 1) {
    const half = size >> 1;
    const phase = (-2 * Math.PI) / size;
    const wr = Math.cos(phase);
    const wi = Math.sin(phase);
    for (let i = 0; i < n; i += size) {
      let wRe = 1;
      let wIm = 0;
      for (let k = 0; k < half; k += 1) {
        const even = i + k;
        const odd = i + k + half;
        const tRe = wRe * re[odd] - wIm * im[odd];
        const tIm = wRe * im[odd] + wIm * re[odd];
        re[odd] = re[even] - tRe;
        im[odd] = im[even] - tIm;
        re[even] += tRe;
        im[even] += tIm;
        const nwRe = wRe * wr - wIm * wi;
        wIm = wRe * wi + wIm * wr;
        wRe = nwRe;
      }
    }
  }
}

function powerSpectrumMagnitude(re, im) {
  const half = re.length >> 1;
  const mag = new Array(half);
  for (let k = 0; k < half; k += 1) {
    mag[k] = re[k] * re[k] + im[k] * im[k];
  }
  return mag;
}

/**
 * @param {number[]} values — velocity or displacement samples
 * @param {number[]} times — seconds, same length
 * @param {number} numBins — histogram bar count (8–64)
 * @param {number} [maxHz] — upper frequency (default Nyquist)
 * @returns {{ bins: Array<{ freqMin: number, freqMax: number, durationSec: number, percent: number }>, sampleRateHz: number, maxHz: number, totalDurationSec: number } | null}
 */
export function computeFrequencyDurationHistogram(values, times, numBins, maxHz = null) {
  if (!values?.length || !times?.length || values.length !== times.length) return null;
  const binsWanted = Math.max(4, Math.min(64, Math.round(numBins) || 16));
  const dtMed = medianDt(times);
  if (dtMed == null) return null;

  const sampleRateHz = Math.max(20, Math.min(2000, 1 / dtMed));
  const uniform = resampleUniform(values, times, sampleRateHz);
  if (uniform.length < MIN_WINDOW * 2) return null;

  const winSize = nextPow2(Math.min(DEFAULT_WINDOW, uniform.length));
  const hop = Math.max(1, Math.floor(winSize * HOP_RATIO));
  const nyquist = sampleRateHz / 2;
  const fMax = maxHz != null && maxHz > 0 ? Math.min(maxHz, nyquist) : nyquist;
  const windowDurationSec = winSize / sampleRateHz;

  const bandPower = new Array(binsWanted).fill(0);
  let windowsUsed = 0;

  for (let start = 0; start + winSize <= uniform.length; start += hop) {
    const re = new Array(winSize);
    const im = new Array(winSize).fill(0);
    let mean = 0;
    for (let i = 0; i < winSize; i += 1) mean += uniform[start + i];
    mean /= winSize;

    for (let i = 0; i < winSize; i += 1) {
      const w = hann(i, winSize);
      re[i] = (uniform[start + i] - mean) * w;
    }

    fft(re, im);
    const mag = powerSpectrumMagnitude(re, im);
    let totalP = 0;
    for (let k = 1; k < mag.length; k += 1) totalP += mag[k];
    if (totalP <= 0) continue;

    windowsUsed += 1;
    for (let k = 1; k < mag.length; k += 1) {
      const freq = (k * sampleRateHz) / winSize;
      if (freq >= fMax) break;
      const binIdx = Math.min(binsWanted - 1, Math.floor((freq / fMax) * binsWanted));
      bandPower[binIdx] += (mag[k] / totalP) * windowDurationSec;
    }
  }

  if (windowsUsed === 0) return null;

  const totalDurationSec = bandPower.reduce((a, b) => a + b, 0);
  if (totalDurationSec <= 0) return null;

  const bins = bandPower.map((durationSec, i) => {
    const freqMin = (i * fMax) / binsWanted;
    const freqMax = ((i + 1) * fMax) / binsWanted;
    return {
      freqMin,
      freqMax,
      durationSec,
      percent: (durationSec / totalDurationSec) * 100,
    };
  });

  return { bins, sampleRateHz, maxHz: fMax, totalDurationSec };
}

/**
 * @param {{ frontVel: (number|null)[], rearVel: (number|null)[], times: number[] }} series
 * @param {number} numBins
 */
export function computeSuspensionFrequencyHistograms(series, numBins) {
  if (!series?.times?.length) return null;
  const { times, frontVel, rearVel } = series;
  const front = frontVel.map((v) => (v != null && Number.isFinite(v) ? v : null));
  const rear = rearVel.map((v) => (v != null && Number.isFinite(v) ? v : null));

  const fillNulls = (arr) => {
    const out = arr.slice();
    let last = 0;
    for (let i = 0; i < out.length; i += 1) {
      if (out[i] != null) last = out[i];
      else out[i] = last;
    }
    return out;
  };

  return {
    front: computeFrequencyDurationHistogram(fillNulls(front), times, numBins),
    rear: computeFrequencyDurationHistogram(fillNulls(rear), times, numBins),
  };
}

export function formatFrequencyRange(freqMin, freqMax) {
  const lo = freqMin < 10 ? freqMin.toFixed(1) : Math.round(freqMin);
  const hi = freqMax < 10 ? freqMax.toFixed(1) : Math.round(freqMax);
  return `${lo}–${hi} Hz`;
}

export function formatDuration(sec) {
  if (sec == null || !Number.isFinite(sec)) return '—';
  if (sec < 1) return `${(sec * 1000).toFixed(0)} ms`;
  if (sec < 60) return `${sec.toFixed(2)} s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toFixed(0).padStart(2, '0')}`;
}
